import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { featureFlags, FeatureFlagKey } from "../featureFlags";
import { API_BASE, getAuthToken } from "../api/agentApi";

type FeatureFlags = Record<string, boolean>;

interface FeatureFlagContextType {
  flags: FeatureFlags;
  setFlag: (key: FeatureFlagKey, value: boolean) => void;
  isEnabled: (key: FeatureFlagKey) => boolean;
  refreshFlags: () => void;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
  undefined
);

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [flags, setFlags] = useState<FeatureFlags>(() => {
    const stored = localStorage.getItem("featureFlags");
    // Merge defaults UNDER stored values so newly-added flags (or changed
    // defaults) are always inherited, while explicit user choices win.
    return stored ? { ...featureFlags, ...JSON.parse(stored) } : featureFlags;
  });

  const applyServerConfig = useCallback((config: Record<string, unknown>) => {
    if (typeof config.enable_create_tool === "boolean") {
      setFlags((prev) => ({
        ...prev,
        enableCreateTool: Boolean(config.enable_create_tool),
      }));
    }
  }, []);

  const refreshFlags = useCallback(async () => {
    const stored = localStorage.getItem("featureFlags");
    const localFlags = stored ? { ...featureFlags, ...JSON.parse(stored) } : featureFlags;
    setFlags(localFlags);

    const token = getAuthToken();
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        return;
      }
      const config = await response.json();
      applyServerConfig(config);
    } catch (_error) {
      // Keep local/default flags if config cannot be reached.
    }
  }, [applyServerConfig]);

  useEffect(() => {
    refreshFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem("featureFlags", JSON.stringify(flags));
  }, [flags]);

  const setFlag = useCallback((key: FeatureFlagKey, value: boolean) => {
    setFlags((prev) => ({ ...prev, [key]: value }));
  }, []);

  const isEnabled = useCallback((key: FeatureFlagKey) => Boolean(flags[key]), [flags]);

  return (
    <FeatureFlagContext.Provider
      value={{ flags, setFlag, isEnabled, refreshFlags }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider"
    );
  }
  return context;
};
